import type { CommitmentsData, Locale, SectorDefinition } from '../types';
import { chrome } from '../i18n';
import { attainment } from './targets';
import { programStatus } from './programs';

export type AttentionSeverity = 'critical' | 'warning';

export interface AttentionItem {
  id: string;
  severity: AttentionSeverity;
  reason: string;
  title: string;
  detail: string;
  owner?: string;
  due?: string;
  href: string;
  accent: string;
  icon: string;
}

const todayIso = (): string => new Date().toISOString().slice(0, 10);

/**
 * Composes the PM "needs your attention" inbox from exceptions across the
 * platform: overdue/critical alerts, badly off-target KPIs, off-track programs.
 * Sorted by severity then due date, capped for scannability.
 */
export function buildAttention(
  sectors: SectorDefinition[],
  commitments: CommitmentsData | undefined,
  locale: Locale,
  limit = 7,
): AttentionItem[] {
  const c = chrome(locale);
  const today = todayIso();
  const items: AttentionItem[] = [];

  for (const s of sectors) {
    const content = s.content[locale];

    for (const a of content.alerts.items) {
      const overdue = Boolean(a.due && a.state !== 'resolved' && a.due < today);
      if (a.level === 'critical' || overdue) {
        items.push({
          id: `${s.id}-${a.id}`,
          severity: overdue || a.level === 'critical' ? 'critical' : 'warning',
          reason: overdue ? c.reasonOverdue : c.reasonCritical,
          title: a.title,
          detail: a.detail,
          owner: a.owner,
          due: a.due,
          href: s.id,
          accent: s.accent,
          icon: overdue ? 'calendar' : 'alert-circle',
        });
      }
    }

    for (const k of content.kpis) {
      const att = attainment(k);
      if (att !== undefined && att < 75) {
        items.push({
          id: `${s.id}-kpi-${k.id}`,
          severity: 'warning',
          reason: c.reasonOffTarget,
          title: `${content.navLabel} — ${k.label}`,
          detail: `${k.value} ${k.unit}${k.target ? ` · ${c.targetLabel}: ${k.target.label}` : ''} (${att}%)`,
          href: s.id,
          accent: s.accent,
          icon: 'target',
        });
      }
    }
  }

  for (const p of commitments?.programs ?? []) {
    if (programStatus(p) === 'off-track') {
      const pc = p.content[locale];
      items.push({
        id: `program-${p.id}`,
        severity: 'critical',
        reason: c.reasonProgramRisk,
        title: pc.name,
        detail: `${c.delivery} ${p.progress}% · ${c.planned} ${p.planned}%`,
        owner: pc.owner,
        due: p.due,
        href: `commitments/${p.id}`,
        accent: p.accent,
        icon: 'flag',
      });
    }
  }

  // severity first; within a severity, national programs outrank sector items; then by due date
  const sev = { critical: 0, warning: 1 } as const;
  const kindRank = (it: AttentionItem) => (it.href.startsWith('commitments/') ? 0 : 1);
  items.sort(
    (a, b) =>
      sev[a.severity] - sev[b.severity] ||
      kindRank(a) - kindRank(b) ||
      (a.due ?? '9999').localeCompare(b.due ?? '9999'),
  );
  return items.slice(0, limit);
}
